import { ref } from 'vue'
import { useNominatim } from '@/composables/useNominatim'

function haversine(a, b) {
  const R = 6371000
  const dLat = ((b.lat - a.lat) * Math.PI) / 180
  const dLng = ((b.lng - a.lng) * Math.PI) / 180
  const lat1 = (a.lat * Math.PI) / 180
  const lat2 = (b.lat * Math.PI) / 180
  const x =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x))
}

function toLatlng(v) {
  if (!v) return null
  if (Array.isArray(v)) return { lat: v[0], lng: v[1] }
  return { lat: Number(v.lat), lng: Number(v.lng) }
}

// getLineas: función getter o ref que devuelve el array de líneas
export function useTransporteBuscador(getLineas) {
  const { geocodeAddress } = useNominatim()

  const origenLatlng = ref(null)
  const destinoLatlng = ref(null)
  const origenQuery = ref('')
  const destinoQuery = ref('')
  const resultados = ref(null)
  const buscando = ref(false)
  const error = ref('')

  const getLineasValue = () =>
    typeof getLineas === 'function' ? getLineas() : getLineas?.value ?? []

  const geocodificar = async (query, target) => {
    const q = query.trim()
    if (!q) return
    buscando.value = true
    error.value = ''
    try {
      const res = await geocodeAddress(q)
      if (res && res.length > 0) {
        const r = res[0]
        const latlng = { lat: parseFloat(r.lat), lng: parseFloat(r.lon) }
        const label = r.address?.house_number
          ? `${r.address.road} ${r.address.house_number}`.trim()
          : r.address?.road || r.display_name?.split(',')[0] || q
        if (target === 'origen') {
          origenLatlng.value = latlng
          origenQuery.value = label
        } else {
          destinoLatlng.value = latlng
          destinoQuery.value = label
        }
      } else {
        error.value = 'No se encontró la dirección'
      }
    } catch {
      error.value = 'Error al buscar la dirección'
    } finally {
      buscando.value = false
    }
  }

  const buscar = (radio = 400) => {
    error.value = ''
    if (!origenLatlng.value || !destinoLatlng.value) {
      error.value = 'Ingresá origen y destino'
      return
    }

    const lineas = getLineasValue()
    const paradasOrigen = []
    const paradasDestino = []

    for (const linea of lineas) {
      for (const sentido of ['ida', 'vuelta']) {
        const paradas = linea[sentido]?.paradas || []
        for (const parada of paradas) {
          const ll = toLatlng(parada.latlng)
          if (!ll) continue
          const dO = haversine(origenLatlng.value, ll)
          const dD = haversine(destinoLatlng.value, ll)
          if (dO < radio) paradasOrigen.push({ parada, linea, sentido, dist: Math.round(dO) })
          if (dD < radio) paradasDestino.push({ parada, linea, sentido, dist: Math.round(dD) })
        }
      }
    }

    // Rutas directas: misma línea tiene paradas cerca de origen y destino
    const directas = []
    const vistasDirectas = new Set()
    for (const po of paradasOrigen) {
      for (const pd of paradasDestino) {
        if (po.linea.id !== pd.linea.id) continue
        const key = po.linea.id
        if (vistasDirectas.has(key)) continue
        vistasDirectas.add(key)
        directas.push({
          tipo: 'directo',
          linea: po.linea,
          sentido: po.sentido,
          paradaOrigen: po.parada,
          paradaDestino: pd.parada,
          distOrigen: po.dist,
          distDestino: pd.dist,
        })
      }
    }

    // Rutas con trasbordo: L1 desde origen → parada común → L2 hasta destino
    const conTransbordo = []
    const vistasTransbordo = new Set()
    for (const po of paradasOrigen) {
      for (const pd of paradasDestino) {
        if (po.linea.id === pd.linea.id) continue
        const key = `${po.linea.id}→${pd.linea.id}`
        if (vistasTransbordo.has(key)) continue

        const paradasL1 = po.linea[po.sentido]?.paradas || []
        const paradasL2 = pd.linea[pd.sentido]?.paradas || []
        let encontrado = false
        for (const pt1 of paradasL1) {
          if (encontrado) break
          for (const pt2 of paradasL2) {
            const ll1 = toLatlng(pt1.latlng)
            const ll2 = toLatlng(pt2.latlng)
            if (!ll1 || !ll2) continue
            if (haversine(ll1, ll2) < 300) {
              vistasTransbordo.add(key)
              conTransbordo.push({
                tipo: 'trasbordo',
                lineaIda: po.linea,
                sentidoIda: po.sentido,
                paradaIda: po.parada,
                paradaTransbordoBajada: pt1,
                lineaVuelta: pd.linea,
                sentidoVuelta: pd.sentido,
                paradaTransbordoSubida: pt2,
                paradaDestino: pd.parada,
                distOrigen: po.dist,
                distDestino: pd.dist,
              })
              encontrado = true
              break
            }
          }
        }
      }
    }

    resultados.value = {
      directas,
      conTransbordo,
      sinResultados: !directas.length && !conTransbordo.length,
    }
  }

  const limpiar = () => {
    origenLatlng.value = null
    destinoLatlng.value = null
    origenQuery.value = ''
    destinoQuery.value = ''
    resultados.value = null
    error.value = ''
  }

  return {
    origenLatlng,
    destinoLatlng,
    origenQuery,
    destinoQuery,
    resultados,
    buscando,
    error,
    geocodificar,
    buscar,
    limpiar,
  }
}
